import { Injectable } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpContextToken
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';

/**
 * Route fragments whose screens must load "softly" — the factory floor boards
 * and the driver portal are always-on / kiosk-style views where a full-screen
 * spinner overlay holding up the screen is unacceptable. Any HTTP call made
 * while one of these routes is active opts out of the global loader, without
 * each call site having to pass the skip flag.
 */
const SILENT_ROUTE_FRAGMENTS = [
  'factory-display',
  'factory-tv',
  'factory-touch',
  'driver-portal'
];

/** Header name that opts a request out of the global loader. Strip from the
 *  outgoing request once seen — the server doesn't need to know about it. */
export const SKIP_LOADER_HEADER = 'X-Skip-Loader';

/** HttpContext token for the same opt-out, for callers that prefer the
 *  HttpContext API over header-stuffing. Both forms are honoured. */
export const SKIP_LOADER = new HttpContextToken<boolean>(() => false);

/**
 * Global request-tracking interceptor that drives ngx-spinner. Every
 * outgoing HTTP call increments an in-flight counter; the spinner is shown
 * the moment the counter goes 0 -> 1 and hidden when it returns to 0.
 *
 * Why a counter instead of show()/hide() per request: when several parallel
 * requests are in flight (e.g. orders + customers + view-groups firing
 * together from a component init), hiding on the first response would
 * dismiss the loader prematurely. The counter keeps the spinner up until
 * the last request settles.
 *
 * Opt-out: requests that carry the X-Skip-Loader header (or the
 * SKIP_LOADER context token) skip the counter entirely. Use this for
 * background polling, autosave, dashboard refresh — anything that should
 * happen invisibly without throwing up a full-screen overlay.
 */
@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private active = 0;

  constructor(private spinner: NgxSpinnerService, private router: Router) {}

  /** True when the current route is one of the soft-loading screens. */
  private isSilentRoute(): boolean {
    const url = this.router.url || '';
    return SILENT_ROUTE_FRAGMENTS.some(fragment => url.indexOf(fragment) !== -1);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Opt-out via header, context token, or being on a soft-loading route. The
    // header form is stripped before the request leaves the browser so we don't
    // leak internal flags to the backend.
    if (req.headers.has(SKIP_LOADER_HEADER) || req.context.get(SKIP_LOADER) || this.isSilentRoute()) {
      const cleaned = req.headers.has(SKIP_LOADER_HEADER)
        ? req.clone({ headers: req.headers.delete(SKIP_LOADER_HEADER) })
        : req;
      return next.handle(cleaned);
    }

    if (this.active === 0) {
      this.spinner.show();
    }
    this.active++;

    return next.handle(req).pipe(
      finalize(() => {
        // Math.max guard: defends against any weird double-decrement (e.g. a
        // canceled request that's already been counted out by a parallel
        // teardown). Without it a stray underflow would leak the spinner
        // forever on the next request.
        this.active = Math.max(0, this.active - 1);
        if (this.active === 0) {
          this.spinner.hide();
        }
      })
    );
  }
}
